import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { ENROL_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import * as Analytics from 'analytics/analytics'
import { loyaltyContext } from 'analytics/snowplow/eventContexts/loyaltyContext'
import requestLoyaltyAccountEnrolment from 'api/loyalty/requestLoyaltyAccountEnrolment'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import config from 'constants/config'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import isValidLoyaltyRegion from 'api/lib/isValidLoyaltyRegion'

interface EnrolLoyaltyAccount {
  accountMemberId?: string
  token?: string
  enableSnackbar?: boolean
  enrolment?: App.LoyaltyEnrolmentType | undefined
}

/**
 * Enrols a user in the loyalty program
 *
 * @param {EnrolLoyaltyAccount} params - The enrollment parameters
 * @param {string} [params.accountMemberId] - Optional member ID to enrol. If not provided, uses the current authenticated user's member ID
 * @param {string} [params.token] - Optional authentication token for the enrollment API request
 * @param {boolean} [params.enableSnackbar] - Whether to show snackbar notifications (currently unused in implementation)
 * @param {App.LoyaltyEnrolmentType} [params.enrolment] - The type of enrollment (e.g., 'newUserRegistration', 'existingUserRegistration')
 *
 * @returns {AppAction} A Redux thunk action that dispatches the enrollment API call
 *
 * @description
 * This function handles the enrollment of users into the loyalty program. It:
 * - Validates that loyalty is enabled and the user doesn't already have an account
 * - Checks if the user's region is valid for loyalty enrollment
 * - Makes an API call to enrol the user with their member ID and region
 * - Shows appropriate success messages based on enrollment type
 * - Tracks analytics events for the enrollment
 * - Sends messages to mobile WebView if in headless mode
 *
 * The function will exit early without taking action if:
 * - Loyalty is not enabled for the current state
 * - No member ID is available
 * - User already has a loyalty account
 * - A loyalty account fetch is in progress or has errored
 * - The user's region is not valid for loyalty
 *
 * @example
 * // Enrol current authenticated user
 * dispatch(enrolLoyaltyAccount({ enrolment: 'newUserRegistration' }))
 */
function enrolLoyaltyAccount({ accountMemberId, token, enrolment }: EnrolLoyaltyAccount): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = accountMemberId ?? state.auth.account.memberId
    const givenName = state.auth.account.givenName
    const headlessMode = state.system.headlessMode
    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const loyaltyAccount = state.loyalty.account
    const regionCode = state.geo.currentRegionCode

    let enrolled = false

    if (
      !getIsLoyaltyEnabled(state) ||
      !memberId ||
      hasLoyaltyAccount ||
      loyaltyAccount.fetching ||
      loyaltyAccount.error ||
      !isValidLoyaltyRegion(regionCode)
    ) return

    dispatch({
      type: API_CALL,
      api: ENROL_LOYALTY_ACCOUNT,
      enrolment,
      request: () => requestLoyaltyAccountEnrolment({
        customerId: memberId,
        region: regionCode,
      }, token)
        .then((result) => {
          enrolled = true
          let analyticsAction = 'default'

          if (enrolment === 'newUserRegistration') {
            analyticsAction = 'sign_up'
            showSnackbar(
              `Welcome to ${config.title} and your new ${getLoyaltyProgramDisplayName()} membership${givenName ? `, ${givenName}` : ''}. Start earning today.`,
              'success',
            )
          }

          if (enrolment === 'existingUserRegistration') {
            analyticsAction = 'opt_in'
            showSnackbar(
              `Welcome to your new ${getLoyaltyProgramDisplayName()} membership${givenName ? `, ${givenName}` : ''}. Start earning today.`,
              'success',
            )
          }

          if (result?.accountNumber) {
            Analytics.trackClientEvent({
              action: analyticsAction,
              category: 'loyalty_sign_up',
              subject: result.accountNumber + '_' + result.tier,
              type: 'operational',
              options: {
                contextsToSkip: [
                  'loyalty',
                ],
              },
              context: [
                loyaltyContext(result),
              ],
            },
            )
          }
          return result
        })
        .catch((err) => {
          showSnackbar(
            'Please try again later or contact us for assistance.',
            'critical',
            { heading: 'Oops! Something went wrong' },
          )

          throw err
        })
        .finally(() => {
          // Send message to mobile WebView
          if (headlessMode) {
            appViewMessageHandler('luxLoyalty', { enrolled })
          }
        }),
    })
  }
}

export default enrolLoyaltyAccount
